import { BasePagination, HStack, IconButton, Text } from '@elkaso-app/web-design';
import { ICrmBrandContactDetailsData, useGetCrmBrandContactDetailsApi } from 'apis/crm-brand-contact-details';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { BrandFilterButton } from '../components/filters/brand-filter';
import { ContactInfoFilterButton } from '../components/filters/contact-info-filter';
import { StatusFilterButton } from '../components/filters/status-filter';
import { CreateContactDetailsModal } from '../components/modals/create-contact-details-modal';
import { DeleteContactDetailsModal } from '../components/modals/delete-contact-details-modal';
import { EditContactDetailsModal } from '../components/modals/edit-contact-details-modal';
import { ActionsCell, IActionsCell } from '../components/table-cells/actions-cell';
import { ContactInfoCell, IContactInfoCell } from '../components/table-cells/contact-info-cell';
import { DateCell, IDateCell } from '../components/table-cells/date-cell';
import { IStatusCell, StatusCell } from '../components/table-cells/status-cell';

const CrmBrandContactDetailsListScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const setPaginationParams = useSetPaginationParamsV1();

  const [selectedContactDetails, setSelectedContactDetails] = useState<ICrmBrandContactDetailsData | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data, isFetching, isLoading } = useGetCrmBrandContactDetailsApi();

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Brand Contact Details'));
  }, [dispatch]);

  // Memoized handlers to prevent recreating on every render
  const handleEdit = useCallback((contactDetails: ICrmBrandContactDetailsData) => {
    setSelectedContactDetails(contactDetails);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((contactDetails: ICrmBrandContactDetailsData) => {
    setSelectedContactDetails(contactDetails);
    setIsDeleteModalOpen(true);
  }, []);

  // Memoized modal close handlers
  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedContactDetails(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedContactDetails(null);
  }, []);

  const handleCreateModalOpen = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  // Memoized columns to prevent recreation
  const columns = useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Brand'
            filterButtonComponent={<BrandFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='brandName' />}
          />
        ),
        accessor: 'brand',
        width: 200,
        Cell: ({ row }: CellProps<any, any>) => (
          <HStack spacing={3}>
            {row.original.brand?.image && (
              <img
                src={row.original.brand.image}
                alt={row.original.brand.name}
                style={{ width: '32px', height: '32px', borderRadius: '4px' }}
              />
            )}
            <Text fontWeight='semibold' fontSize='md' color='gray.800'>
              {row.original.brand?.name || 'Unknown Brand'}
            </Text>
          </HStack>
        ),
      },
      {
        Header: <TableHeader name='Contact Information' filterButtonComponent={<ContactInfoFilterButton />} />,
        accessor: 'contactName',
        width: 280,
        minWidth: 250,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IContactInfoCell>
            as={ContactInfoCell}
            contactName={row.original.contactName}
            designation={row.original.designation}
            email={row.original.email}
            phoneNumber={row.original.phoneNumber}
          />
        ),
      },
      {
        Header: <TableHeader name='Created & Updated' />,
        accessor: 'createdAt',
        width: 200,
        minWidth: 180,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IDateCell>
            as={DateCell}
            createdAt={row.original.createdAt}
            updatedAt={row.original.updatedAt}
          />
        ),
      },
      {
        Header: <TableHeader name='Verification Status' filterButtonComponent={<StatusFilterButton />} />,
        accessor: 'isVerified',
        width: 180,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IStatusCell>
            as={StatusCell}
            isVerified={row.original.isVerified}
            contactId={row.original.id}
          />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'id',
        width: 120,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IActionsCell>
            as={ActionsCell}
            contactDetails={row.original}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ),
      },
    ],
    [handleEdit, handleDelete] // Add dependencies for memoized handlers
  );

  // Memoized table data
  const tableData = useMemo(() => data?.data || [], [data?.data]);

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='CRM Brand Contact Details' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <IconButton
            aria-label='Add Contact Details'
            icon={<FiPlus />}
            colorScheme='red'
            onClick={handleCreateModalOpen}
          />
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={data?.pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={data?.total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>

      {/* Self-contained modals */}
      <CreateContactDetailsModal isOpen={isCreateModalOpen} onClose={handleCreateModalClose} />

      {selectedContactDetails && (
        <>
          <EditContactDetailsModal
            isOpen={isEditModalOpen}
            onClose={handleEditModalClose}
            contactDetails={selectedContactDetails}
          />
          <DeleteContactDetailsModal
            isOpen={isDeleteModalOpen}
            onClose={handleDeleteModalClose}
            contactDetails={selectedContactDetails}
          />
        </>
      )}
    </BaseTableContainer>
  );
};

export default CrmBrandContactDetailsListScreen;
